import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { Package, Plus, ShoppingBag, TrendingUp, IndianRupee, BarChart3, Edit, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import ImageUploader from "@/components/upload/ImageUploader";
import SellerOrders from "@/components/seller/SellerOrders";

const SellerDashboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/sellers/me/profile").then((r) => setProfile(r.data)).catch(() => setProfile(null));
    api.get("/sellers/me/products").then((r) => setProducts(r.data));
    api.get("/orders/me/seller").then((r) => setOrders(r.data));
    api.get("/sellers/me/analytics").then((r) => setAnalytics(r.data));
  }, [user]);

  if (!user) return <div className="container-fah py-20 text-center"><Link to="/login" className="text-primary underline">Please login</Link></div>;
  if (user.role !== "seller") {
    return (
      <div className="container-fah py-20 text-center" data-testid="seller-not-registered">
        <h2 className="font-display text-2xl font-bold">Become a Seller</h2>
        <p className="text-muted-foreground mt-2">Complete your seller registration to access this dashboard.</p>
        <Link to="/seller/register" className="mt-5 inline-block bg-primary text-white font-semibold py-2.5 px-6 rounded-lg">Register as Seller</Link>
      </div>
    );
  }

  return (
    <div className="container-fah py-8" data-testid="page-seller-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Seller Dashboard</h1>
          <p className="text-sm text-muted-foreground">Welcome back{profile ? `, ${profile.shop_name}` : ""}</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="bg-secondary text-secondary-foreground font-bold py-2.5 px-4 rounded-lg flex items-center gap-2" data-testid="btn-add-product">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={IndianRupee} label="Revenue" value={analytics ? formatINR(analytics.total_revenue) : "—"} sub="last 6 months" />
        <Kpi icon={ShoppingBag} label="Orders" value={analytics?.total_orders ?? 0} sub="all time" />
        <Kpi icon={Package} label="Products" value={products.length} sub="active" />
        <Kpi icon={TrendingUp} label="Avg Rating" value={profile?.rating || "—"} sub={`${profile?.verified ? "Verified" : "Pending"} seller`} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border mb-5 overflow-x-auto">
        {[["overview", "Overview"], ["products", "Products"], ["orders", "Orders"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`seller-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="seller-overview">
          <div className="card-fah p-5">
            <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-5 h-5 text-primary" /><h3 className="font-display font-bold">Monthly Revenue</h3></div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.monthly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card-fah p-5">
            <h3 className="font-display font-bold mb-3">Recent Orders</h3>
            {orders.slice(0, 5).length === 0 ? <div className="text-sm text-muted-foreground py-4">No orders yet</div> : (
              <div className="space-y-2">
                {orders.slice(0, 5).map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                    <div>
                      <div className="font-semibold">{o.order_number}</div>
                      <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatINR(o.total)}</div>
                      <div className="text-[10px] uppercase tracking-wide text-primary">{o.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "products" && (
        <ProductsTable products={products} setProducts={setProducts} onEdit={setEditProduct} />
      )}

      {tab === "orders" && <SellerOrders />}

      {addOpen && <AddProductModal onClose={() => setAddOpen(false)} onAdd={(p) => setProducts([p, ...products])} />}
      {editProduct && (
        <AddProductModal
          editProduct={editProduct}
          onClose={() => setEditProduct(null)}
          onUpdate={(updated) => setProducts(products.map((p) => (p.id === updated.id ? updated : p)))}
        />
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="card-fah p-4">
    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide"><Icon className="w-4 h-4" /> {label}</div>
    <div className="font-display text-2xl font-bold mt-1">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
  </div>
);

const ProductsTable = ({ products, setProducts, onEdit }) => {
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    setProducts(products.filter((p) => p.id !== id));
    toast.success("Product deleted");
  };
  return (
    <div className="card-fah overflow-x-auto" data-testid="seller-products">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left">
          <tr>{["Product", "Category", "Price", "Stock", "Actions"].map((h) => <th key={h} className="px-4 py-3 text-xs uppercase tracking-wide">{h}</th>)}</tr>
        </thead>
        <tbody>
          {products.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">No products listed yet. Click "Add Product" to get started.</td></tr>}
          {products.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <img src={p.images?.[0]} alt={p.name} className="w-10 h-10 rounded object-cover bg-muted" />
                  <div className="min-w-0">
                    <div className="font-semibold line-clamp-1">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.brand}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 capitalize">{p.category.replace(/-/g, " ")}</td>
              <td className="px-4 py-3 font-semibold">{formatINR(p.price)}</td>
              <td className="px-4 py-3">{p.stock}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => onEdit(p)} className="text-primary hover:bg-primary/10 p-1.5 rounded" title="Edit" data-testid={`edit-prod-${p.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded" title="Delete" data-testid={`del-prod-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AddProductModal = ({ onClose, onAdd, onUpdate, editProduct }) => {
  const isEdit = !!editProduct;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: editProduct.name,
          category: editProduct.category,
          description: editProduct.description,
          price: String(editProduct.price),
          mrp: String(editProduct.mrp),
          stock: String(editProduct.stock),
          unit: editProduct.unit,
          brand: editProduct.brand,
          images: editProduct.images?.length ? editProduct.images : [],
        }
      : {
          name: "", category: "seeds", description: "", price: "", mrp: "", stock: "", unit: "", brand: "",
          images: [],
        }
  );
  const [busy, setBusy] = useState(false);
  const cats = ["seeds", "pesticides", "fungicides", "insecticides", "herbicides", "bio-fertilizers", "plant-growth-promoters", "agriculture-tools", "irrigation-products", "sprayers", "farm-machinery"];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.images || form.images.length === 0) { toast.error("Please upload at least one product image"); return; }
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price), mrp: Number(form.mrp), stock: Number(form.stock) };
      if (isEdit) {
        const { data } = await api.put(`/products/${editProduct.id}`, payload);
        onUpdate(data); toast.success("Product updated"); onClose();
      } else {
        const { data } = await api.post("/products", payload);
        onAdd(data); toast.success("Product added"); onClose();
      }
    } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid={isEdit ? "edit-product-modal" : "add-product-modal"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-border flex justify-between items-center sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{isEdit ? "Edit Product" : "Add New Product"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form onSubmit={submit} className="p-5 grid sm:grid-cols-2 gap-3">
          <Labeled label="Product Name" className="sm:col-span-2">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-name" />
          </Labeled>
          <Labeled label="Category">
            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-category">
              {cats.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ")}</option>)}
            </select>
          </Labeled>
          <Labeled label="Brand"><input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-brand" /></Labeled>
          <Labeled label="Selling Price ₹"><input required type="number" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-price" /></Labeled>
          <Labeled label="MRP ₹"><input required type="number" min="1" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-mrp" /></Labeled>
          <Labeled label="Stock"><input required type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-stock" /></Labeled>
          <Labeled label="Unit (e.g. 1kg, 500ml)"><input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="prod-unit" /></Labeled>
          <Labeled label="Description" className="sm:col-span-2">
            <textarea
              required
              rows={7}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white font-mono leading-relaxed"
              data-testid="prod-desc"
              placeholder={`Write a clear product description. Use blank lines for spacing and "-" for bullet points.\n\nExample:\nBroad-spectrum contact fungicide for crop protection.\n\nKey Highlights:\n- Contact + protective action\n- Multisite mode, safe for long-term use\n- Supplies Zinc & Manganese\n\nApplication:\nFoliar spray or seed treatment.`}
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Tip: Blank line = paragraph gap · Lines starting with <b>-</b> or <b>•</b> become bullets · Lines ending with <b>:</b> become sub-headings.
            </div>
          </Labeled>
          <Labeled label="Product Images" className="sm:col-span-2">
            <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} maxFiles={10} />
          </Labeled>
          <button disabled={busy} type="submit" className="sm:col-span-2 bg-primary text-white font-semibold py-2.5 rounded-lg disabled:opacity-50" data-testid="btn-save-product">{busy ? "Saving…" : isEdit ? "Save Changes" : "Add Product"}</button>
        </form>
      </div>
    </div>
  );
};

const Labeled = ({ label, className = "", children }) => (
  <div className={className}>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default SellerDashboard;
